import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useWebAccountsScope } from '@/hooks/useWebAccountsScope';
import { scopeWebhookStatusToMeta } from '@/lib/whatsapp-web-signals';

export interface WebhookError {
  type: 'authentication' | 'config' | 'network' | 'unknown';
  message: string;
  timestamp: Date;
  count: number;
  tenantId?: string;
}

export interface WebhookHealthStatus {
  isHealthy: boolean;
  lastError?: WebhookError;
  recentErrors: WebhookError[];
  successRate: number;
  // N-10: how many deliveries `successRate` is computed over. 0 means nothing was measured
  // in the window -- the caller must not read that as "100% healthy" (it used to).
  totalCalls?: number;
  lastSuccessfulRequest?: Date;
  authenticationFailing: boolean;
  /** A QR account's bridge does not answer (its lease ran out). Not a Meta webhook problem. */
  bridgeDown?: boolean;
}

interface UseWebhookErrorDetectionOptions {
  tenantId?: string;
  pollInterval?: number;
  showToasts?: boolean;
}

export function useWebhookErrorDetection({
  tenantId,
  pollInterval = 60000, // 1 minute
  showToasts = true
}: UseWebhookErrorDetectionOptions = {}) {
  const { t } = useTranslation('platformAdmin');
  const { toast } = useToast();
  const [status, setStatus] = useState<WebhookHealthStatus>({
    isHealthy: false, // Start as unhealthy until verified by health check
    recentErrors: [],
    successRate: 0,
    authenticationFailing: false
  });
  const [loading, setLoading] = useState(true);
  const pollIntervalRef = useRef<NodeJS.Timeout>();
  const lastErrorCountRef = useRef<number>(0); // Start with no errors

  const checkWebhookHealth = async () => {
    try {
      // Use Supabase analytics query to get webhook logs
      return await checkWebhookHealthWithAnalytics();
    } catch (error) {
      console.error('Error checking webhook health:', error);
      setStatus(prev => ({
        ...prev,
        isHealthy: false,
        lastError: {
          type: 'unknown',
          message: t('webhookHealth.checkFailed'),
          timestamp: new Date(),
          count: 1
        }
      }));
    }
  };

  const checkWebhookHealthWithAnalytics = async () => {
    // Use Supabase Analytics API to get real Edge Function logs
    return await checkEdgeFunctionLogs();
  };

  const checkEdgeFunctionLogs = async () => {
    try {
      // Use dedicated webhook health check Edge Function
      console.log('🔍 Checking webhook health via Edge Function');
      
      const { data, error } = await supabase.functions.invoke('webhook-health-check', {
        body: { tenantId }
      });

      if (error) {
        console.warn('Webhook health check failed:', error.message);
        setLoading(false);
        return await checkBasedOnWebhookLogs();
      }

      // Debug: Log the actual response structure
      console.log('🔍 Raw Edge Function Response:', { data, error });

      // Handle the actual response structure from the edge function
      // The edge function returns the health data under the 'health' property
      if (data?.health && typeof data.health === 'object') {
        const health = data.health;
        
        console.log(`🔍 Webhook Health Check Result:`, {
          isHealthy: health.isHealthy,
          authenticationFailing: health.authenticationFailing,
          successRate: typeof health.successRate === 'number' ? health.successRate.toFixed(1) + '%' : health.successRate,
          totalCalls: health.totalCalls
        });

        setStatus(prev => ({
          ...prev,
          isHealthy: health.isHealthy || false,
          authenticationFailing: health.authenticationFailing || false,
          successRate: health.successRate || 0,
          totalCalls: typeof health.totalCalls === 'number' ? health.totalCalls : undefined,
          recentErrors: health.recentErrors || [],
          lastSuccessfulRequest: health.lastSuccessfulRequest ? new Date(health.lastSuccessfulRequest) : undefined,
          lastError: health.lastError ? {
            ...health.lastError,
            timestamp: new Date(health.lastError.timestamp)
          } : undefined
        }));
        
        // Show toast for authentication failures
        if (health.authenticationFailing && showToasts) {
          const currentErrorCount = health.recentErrors?.length || 0;
          if (currentErrorCount > lastErrorCountRef.current) {
            toast({
              title: t('webhookHealth.authFailedTitle'),
              description: t('webhookHealth.authFailedDesc'),
              variant: "destructive"
            });
          }
          lastErrorCountRef.current = currentErrorCount;
        }
        
        setLoading(false);
        return;
      }

      // Fallback if Edge Function response is unexpected
      console.warn('Unexpected Edge Function response format:', data);
      setLoading(false);
      return await checkBasedOnWebhookLogs();
    } catch (error) {
      console.error('Edge function health check failed, falling back:', error);
      setLoading(false);
      return await checkBasedOnWebhookLogs();
    }
  };

  const checkBasedOnWebhookLogs = async () => {
    try {
      // Check recent webhook log entries as backup method
      const { data: webhookLogs, error } = await supabase
        .from('whatsapp_webhook_logs')
        .select('*')
        .gte('created_at', new Date(Date.now() - 10 * 60 * 1000).toISOString())
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) {
        console.warn('Could not fetch webhook logs:', error.message);
        // If we can't check anything, mark as unhealthy rather than assuming healthy
        setStatus(prev => ({
          ...prev,
          isHealthy: false,
          authenticationFailing: false,
          successRate: 0,
          recentErrors: [],
          lastError: {
            type: 'unknown',
            message: t('webhookHealth.verifyFailed'),
            timestamp: new Date(),
            count: 1
          }
        }));
        return;
      }

      const logs = webhookLogs || [];
      
      if (logs.length === 0) {
        // N-10: ten quiet minutes is not evidence of health, so this stops asserting
        // "100%" out of silence -- totalCalls: 0 is the signal the alert reads to show
        // "not enough data" instead of a fabricated rate. `isHealthy` stays true here on
        // purpose: this fallback only runs when the real health check already failed, and
        // a quiet channel is not the same claim as a broken one -- flipping it to false
        // would raise the alarm banner on every slow period instead of only real failures.
        console.log('No recent webhook activity in the fallback window -- nothing measured yet');
        setStatus(prev => ({
          ...prev,
          isHealthy: true,
          authenticationFailing: false,
          successRate: 0,
          totalCalls: 0,
          recentErrors: []
        }));
        return;
      }

      // If we have recent webhook logs without errors, system is healthy
      console.log('✅ Recent webhook activity detected - system healthy');
      setStatus(prev => ({
        ...prev,
        isHealthy: true,
        authenticationFailing: false,
        successRate: 100,
        totalCalls: logs.length,
        recentErrors: [],
        lastSuccessfulRequest: new Date(logs[0].created_at)
      }));

    } catch (error) {
      console.error('Fallback webhook health check failed:', error);
      // Ultimate fallback - mark as unhealthy when we can't verify health
      setStatus(prev => ({
        ...prev,
        isHealthy: false,
        authenticationFailing: false,
        successRate: 0,
        recentErrors: [],
        lastError: {
          type: 'unknown',
          message: t('webhookHealth.verificationFailed'),
          timestamp: new Date(),
          count: 1
        }
      }));
    }
  };

  useEffect(() => {
    if (!tenantId) return;
    console.log('🚀 Initializing real webhook health monitoring');
    
    // Initial check
    checkWebhookHealth().finally(() => setLoading(false));

    // Set up polling
    if (pollInterval > 0) {
      pollIntervalRef.current = setInterval(() => {
        checkWebhookHealth();
      }, pollInterval);
    }

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [tenantId, pollInterval, showToasts]);

  const forceCheck = async () => {
    setLoading(true);
    try {
      await checkWebhookHealth();
    } finally {
      setLoading(false);
    }
  };

  // The Meta webhook check only speaks for Meta accounts. A QR account is judged by its session
  // and bridge lease; a space with only QR accounts never shows a Meta credential failure.
  const scope = useWebAccountsScope(tenantId);
  const scopedStatus = scope.known ? scopeWebhookStatusToMeta(status, scope) : status;

  return {
    status: scopedStatus,
    // Until the kind of accounts is known the alarm stays hidden instead of flashing the Meta one.
    loading: loading || (!!tenantId && !scope.known),
    forceCheck: async () => {
      await Promise.all([forceCheck(), scope.reload()]);
    },
    checkHealth: checkWebhookHealth
  };
}